import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { apiRequest } from "../api";

export interface ClientLoginData {
  /** E-mail ou telefone cadastrado — o backend detecta qual dos dois é. */
  identifier: string;
  password: string;
  subdomain?: string;
}

export interface ClientRegisterData {
  name: string;
  email?: string;
  phone?: string;
  password: string;
  subdomain?: string;
  tenant?: string;
}

export interface ClientUser {
  _id: string;
  name: string;
  email: string;
  clientId: string;
  organizationId: string;
  status: string;
  preferences?: { pwaInstallPromptDismissed?: boolean };
}

export interface ClientLoginResponse {
  accessToken: string;
  user: ClientUser;
}

export type TicketAccessMode = "open" | "restricted" | "hybrid";

export interface TenantSettingsResponse {
  ticketAccessMode: TicketAccessMode;
  selfRegistrationEnabled: boolean;
}

export interface ClientRegisterResponse {
  msg?: string;
  accessToken?: string;
  user?: ClientUser;
  pendingApproval?: boolean;
  /** Telefone já pertence a alguém sem senha — mandamos um link de confirmação pelo WhatsApp. */
  requiresWhatsappConfirmation?: boolean;
  client?: unknown;
}

export const clientAuthService = {
  async fetchTenantSettings(
    subdomain: string
  ): Promise<[TenantSettingsResponse | null, { msg: string; code?: string } | null]> {
    const sub = (subdomain || "").trim();
    if (!sub) return [null, { msg: "Subdomínio obrigatório" }];
    return apiRequest<TenantSettingsResponse>({
      loaderKey: "tenantSettings",
      method: "get",
      url: "/app/client-auth/tenant-settings",
      params: { subdomain: sub },
      ignoreError: true,
    });
  },

  async login(
    data: ClientLoginData & { tenant?: string },
    options?: { ignoreError?: boolean }
  ): Promise<[ClientLoginResponse | null, { msg: string; code?: string } | null]> {
    const payload = { ...data };
    if (payload.tenant && !payload.subdomain) payload.subdomain = payload.tenant;
    const [response, error] = await apiRequest<ClientLoginResponse>({
      loaderKey: "clientLogin",
      method: "post",
      url: "/app/client-auth/login",
      data: payload,
      ignoreError: options?.ignoreError,
    });
    if (response?.accessToken) {
      localStorage.setItem("client_token", response.accessToken);
      localStorage.setItem("client_user", JSON.stringify(response.user));
      localStorage.removeItem("client_impersonated");
      const t = payload.subdomain || payload.tenant;
      setClientTenant(t || null);
    }
    return [response, error];
  },

  async register(
    data: ClientRegisterData
  ): Promise<[ClientRegisterResponse | null, { msg: string; code?: string } | null]> {
    const payload = { ...data };
    if (payload.tenant && !payload.subdomain) payload.subdomain = payload.tenant;
    const [response, error] = await apiRequest<ClientRegisterResponse>({
      loaderKey: "clientRegister",
      method: "post",
      url: "/app/client-auth/register",
      data: payload,
    });
    if (response?.accessToken && response.user) {
      localStorage.setItem("client_token", response.accessToken);
      localStorage.setItem("client_user", JSON.stringify(response.user));
      localStorage.removeItem("client_impersonated");
      const t = payload.subdomain || payload.tenant;
      setClientTenant(t || null);
    }
    return [response, error];
  },

  /** Consulta o link de reivindicação sem consumi-lo — usado pra saber se falta pedir e-mail. */
  async getClaimInfo(
    token: string
  ): Promise<[{ name?: string; needsEmail: boolean } | null, { msg: string; code?: string } | null]> {
    return apiRequest<{ name?: string; needsEmail: boolean }>({
      loaderKey: "clientClaimInfo",
      method: "get",
      url: `/app/client-auth/claim/${token}/info`,
      ignoreError: true,
    });
  },

  /** Confirma o link recebido no WhatsApp e define e-mail (se faltar) e senha da conta já existente. */
  async claimAccount(
    token: string,
    password: string,
    email?: string
  ): Promise<[ClientLoginResponse | null, { msg: string; code?: string } | null]> {
    const [response, error] = await apiRequest<ClientLoginResponse>({
      loaderKey: "clientClaimAccount",
      method: "post",
      url: `/app/client-auth/claim/${token}`,
      data: { password, email },
    });
    if (response?.accessToken) {
      localStorage.setItem("client_token", response.accessToken);
      localStorage.setItem("client_user", JSON.stringify(response.user));
      localStorage.removeItem("client_impersonated");
    }
    return [response, error];
  },

  logout() {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_user");
    localStorage.removeItem("client_impersonated");
    window.location.href = `${getClientPortalBase()}/login`;
  },

  getCurrentUser(): ClientUser | null {
    const s = localStorage.getItem("client_user");
    return s ? JSON.parse(s) : null;
  },

  /** true quando esta sessão do portal veio de um agente que clicou em "Acessar como este cliente" —
   * usado pra mostrar o aviso de modo admin e travar o envio de mensagens (o cliente de verdade não
   * pode ficar sem saber que um agente respondeu "como ele"). */
  isImpersonating(): boolean {
    return localStorage.getItem("client_impersonated") === "1";
  },

  getToken(): string | null {
    return localStorage.getItem("client_token");
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem("client_token");
  },

  async updatePreferences(
    prefs: { pwaInstallPromptDismissed?: boolean }
  ): Promise<[{ user: ClientUser } | null, { msg: string; code?: string } | null]> {
    const [response, error] = await apiRequest<{ user: ClientUser }>({
      loaderKey: "clientUpdatePreferences",
      method: "patch",
      url: "/app/client-auth/preferences",
      data: prefs,
    });
    if (response?.user) {
      localStorage.setItem("client_user", JSON.stringify(response.user));
    }
    return [response, error];
  },

  /** Define o e-mail do cliente logado quando ainda não tinha um (ex.: entrou pelo magic link). */
  async setEmail(
    email: string
  ): Promise<[{ user: ClientUser } | null, { msg: string; code?: string } | null]> {
    const [response, error] = await apiRequest<{ user: ClientUser }>({
      loaderKey: "clientSetEmail",
      method: "patch",
      url: "/app/client-auth/email",
      data: { email },
    });
    if (response?.user) {
      localStorage.setItem("client_user", JSON.stringify(response.user));
    }
    return [response, error];
  },
};
